import React, { useEffect, useMemo, useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import moment from "moment";
import { AppBar } from "../../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../../components/desktopUi";
import { DetailTabBar } from "../../../components/financeDetail";
import {
  CenterSpinner,
  EmptyText,
  ExpandableCard,
  M,
  PropCell,
  RowKV,
} from "../../../components/managementUi";
import { getBlockKpiDetailApi } from "../../../api/statisticalReport.api";
import { BLOCK_KPI_STATUS_LABELS, blockKpiStatusUi } from "./BlockKpiList";

// Khớp app: app/(feature)/management/block-kpi/detail.tsx
const TABS = [
  { key: "overview", title: "Tổng quan" },
  { key: "explain", title: "Diễn giải" },
  { key: "evaluation", title: "Đánh giá" },
];

const EXPLAIN_TABS = [
  { key: "recruitment", label: "Tuyển dụng" },
  { key: "attendance", label: "Chuyên cần" },
  { key: "training", label: "Đào tạo" },
  { key: "transaction", label: "Giao dịch" },
];

const fmt = (v) => {
  const n = Number(v || 0);
  return Number.isInteger(n) ? `${n}` : `${Math.round(n * 100) / 100}`;
};
const fmtDate = (v) => (v ? moment(v).format("DD/MM/YYYY") : "-");
const evidenceId = (e, i) => `${e?.employeeId || e?._id || e?.code || i}`;

/** Dòng tiêu chí có chấm trạng thái + điểm/trọng số (khớp CriteriaRow của app). */
const CriteriaRow = ({ label, value, score, weight, active, borderBottom = true }) => (
  <Flex
    align="center"
    gap="12px"
    py="14px"
    borderBottom={borderBottom ? "0.5px solid" : "none"}
    borderColor={M.border}
  >
    <Box w="8px" h="8px" borderRadius="4px" bg={active ? "#2ABA82" : M.border} flexShrink={0} />
    <Box flex="1" minW="0">
      <Text fontSize="12px" fontWeight={600} color={M.baseContent}>
        {label}
      </Text>
      <Text fontSize="12px" color={M.grey200} mt="2px">
        {value}
      </Text>
    </Box>
    <Text fontSize="14px" fontWeight={600} color={active ? "#2ABA82" : M.gray} flexShrink={0}>
      {fmt(score)}/{weight}
    </Text>
  </Flex>
);

/** Dựng danh sách card minh chứng theo tiêu chí (khớp buildExplainCards của app). */
const buildExplainCards = (evidences, selected) => {
  if (!evidences) return [];
  const title = (e) => `${e?.code || "-"} - ${e?.fullName || "-"}`;

  if (selected === "recruitment") {
    const newStaff = (evidences.recruitment?.newStaff || []).map((e, i) => ({
      id: `new-${evidenceId(e, i)}`,
      title: title(e),
      rows: [
        { label: "Mã nhân viên", value: e?.code || "-" },
        { label: "Tên nhân viên", value: e?.fullName || "-" },
        { label: "SĐT", value: e?.phone || "-" },
        { label: "Ngày vào làm", value: fmtDate(e?.startTime) },
        { label: "Phân loại", value: "Tuyển mới" },
      ],
    }));
    const resigned = (evidences.recruitment?.resignedStaff || []).map((e, i) => ({
      id: `res-${evidenceId(e, i)}`,
      title: title(e),
      rows: [
        { label: "Mã nhân viên", value: e?.code || "-" },
        { label: "Tên nhân viên", value: e?.fullName || "-" },
        { label: "SĐT", value: e?.phone || "-" },
        { label: "Ngày nghỉ", value: fmtDate(e?.endTime) },
        { label: "Phân loại", value: "Nghỉ" },
      ],
    }));
    return [...newStaff, ...resigned];
  }

  if (selected === "attendance") {
    const actualIds = new Set((evidences.attendance?.actualWorking || []).map((e, i) => evidenceId(e, i)));
    return (evidences.attendance?.registered || []).map((e, i) => {
      const working = actualIds.has(evidenceId(e, i));
      return {
        id: `att-${evidenceId(e, i)}`,
        title: title(e),
        rows: [
          { label: "Mã nhân viên", value: e?.code || "-" },
          { label: "Tên nhân viên", value: e?.fullName || "-" },
          { label: "SĐT", value: e?.phone || "-" },
          { label: "Số ngày công", value: e?.workingDays !== undefined ? `${e.workingDays}` : "-" },
          { label: "Trạng thái", value: working ? "Đi làm Fulltime thực tế" : "Đăng ký Fulltime" },
        ],
      };
    });
  }

  if (selected === "training") {
    return (evidences.training?.list || []).map((e, i) => ({
      id: `tr-${evidenceId(e, i)}`,
      title: title(e),
      rows: [
        { label: "Mã nhân viên", value: e?.code || "-" },
        { label: "Tên nhân viên", value: e?.fullName || "-" },
        { label: "Trạng thái KPI", value: e?.achieved ? "Đạt KPI" : "Chưa đạt" },
      ],
    }));
  }

  return (evidences.transaction?.list || []).map((t, i) => ({
    id: `tx-${t?.transactionCode || i}`,
    title: `${t?.apartmentCode || "Giao dịch"}${t?.projectName ? ` - ${t.projectName}` : ""}`,
    rows: [
      { label: "Mã căn", value: t?.apartmentCode || "-" },
      { label: "Dự án", value: t?.projectName || "-" },
      { label: "Ngày giao dịch", value: fmtDate(t?.transactionDate) },
    ],
  }));
};

const BlockKpiDetail = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { id: code } = useParams();
  const [search] = useSearchParams();
  const month = Number(search.get("month")) || new Date().getMonth() + 1;
  const year = Number(search.get("year")) || new Date().getFullYear();

  const [tab, setTab] = useState(0);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [explainKey, setExplainKey] = useState("recruitment");
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!code) return;
    setLoading(true);
    getBlockKpiDetailApi({ month, year, code })
      .then((d) => setDetail(d || null))
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [code, month, year]);

  const explainCards = useMemo(
    () => buildExplainCards(detail?.evidences, explainKey),
    [detail?.evidences, explainKey]
  );

  const activeKey = TABS[tab]?.key;

  const renderTab = () => {
    if (loading) return <CenterSpinner />;
    if (!detail) return <EmptyText text="Không tìm thấy thông tin" />;

    const m = detail?.evaluation?.metrics || {};
    const criteria = detail?.criteria || {};
    const cls = detail?.evaluation?.classification || {};
    const ui = blockKpiStatusUi(cls?.code);

    if (activeKey === "overview") {
      const scoreLines = [
        { label: "Tuyển dụng", score: m.recruitmentScore, weight: criteria.recruitmentWeight || 20 },
        { label: "Chuyên cần", score: m.attendanceScore, weight: criteria.attendanceWeight || 20 },
        { label: "Đào tạo", score: m.trainingScore, weight: criteria.trainingWeight || 20 },
        { label: "Giao dịch", score: m.transactionScore, weight: criteria.transactionWeight || 40 },
      ];
      return (
        <Box>
          <PropCell
            label="Đánh giá"
            value={cls?.label || BLOCK_KPI_STATUS_LABELS[cls?.code] || "-"}
            color={ui.color}
          />
          <PropCell label="Tháng" value={`${month}/${year}`} />
          <PropCell label="Tên khối" value={detail?.block?.name} />
          <PropCell label="Mã GĐ khối" value={detail?.block?.managerCode} />
          <PropCell label="Tên GĐ khối" value={detail?.block?.managerName} />
          <PropCell label="Chi nhánh" value={detail?.block?.branchName} />
          <PropCell label="Vùng" value={detail?.block?.areaName} />
          {scoreLines.map((l) => (
            <PropCell key={l.label} label={l.label} value={`${fmt(l.score)}/${l.weight}`} />
          ))}
          <PropCell label="Tổng điểm" value={`${fmt(m.scorePercent ?? m.score)}/100`} color={M.primary} />
          {detail?.evaluation?.note ? (
            <PropCell label="Ghi chú" value={detail.evaluation.note} borderBottom={false} />
          ) : null}
        </Box>
      );
    }

    // Diễn giải: chip chọn tiêu chí + danh sách minh chứng bung/thu.
    if (activeKey === "explain") {
      return (
        <Box>
          <Flex gap="8px" py="12px">
            {EXPLAIN_TABS.map((t) => {
              const active = t.key === explainKey;
              return (
                <Flex
                  key={t.key}
                  as="button"
                  type="button"
                  flex="1"
                  align="center"
                  justify="center"
                  py="8px"
                  borderRadius="8px"
                  border="1px solid"
                  borderColor={active ? M.primary : M.border}
                  bg={active ? M.primary : "white"}
                  onClick={() => {
                    setExplainKey(t.key);
                    setExpanded({});
                  }}
                >
                  <Text fontSize="12px" fontWeight={600} color={active ? "white" : M.baseContent}>
                    {t.label}
                  </Text>
                </Flex>
              );
            })}
          </Flex>

          {explainCards.length === 0 ? (
            <EmptyText />
          ) : (
            <Flex direction="column" gap="10px" pb="32px">
              {explainCards.map((card) => (
                <ExpandableCard
                  key={card.id}
                  open={!!expanded[card.id]}
                  onToggle={() => setExpanded((p) => ({ ...p, [card.id]: !p[card.id] }))}
                  header={
                    <Text fontSize="14px" fontWeight={600} color={M.baseContent} noOfLines={2}>
                      {card.title}
                    </Text>
                  }
                >
                  {card.rows.map((r) => (
                    <RowKV key={r.label} label={r.label} value={r.value} labelFlex="1" />
                  ))}
                </ExpandableCard>
              ))}
            </Flex>
          )}
        </Box>
      );
    }

    // Đánh giá: bảng điểm theo tiêu chí + tổng điểm.
    return (
      <Box pt="8px">
        <CriteriaRow
          label="Tuyển dụng"
          value={`Nhân sự tuyển mới: ${Number(m.newStaffCount || 0)} • Nghỉ: ${Number(m.resignedStaffCount || 0)}`}
          score={m.recruitmentScore}
          weight={criteria.recruitmentWeight || 20}
          active={!!m.hasMetRecruitmentCriterion}
        />
        <CriteriaRow
          label="Chuyên cần"
          value={`Đi làm: ${Number(m.actualFulltimeStaffCount || 0)}/${Number(
            m.registeredFulltimeStaffCount || 0
          )} • ${fmt(m.attendanceRate)}%`}
          score={m.attendanceScore}
          weight={criteria.attendanceWeight || 20}
          active={!!m.hasMetAttendanceCriterion}
        />
        <CriteriaRow
          label="Đào tạo"
          value={`Đạt KPI: ${Number(m.achievedKpiStaffCount || 0)}/${Number(
            m.actualFulltimeStaffCount || 0
          )} • ${fmt(m.trainingRate)}%`}
          score={m.trainingScore}
          weight={criteria.trainingWeight || 20}
          active={!!m.hasMetTrainingCriterion}
        />
        <CriteriaRow
          label="Giao dịch"
          value={`Phát sinh GD: ${Number(m.transactingStaffCount || 0)}/${Number(
            m.actualFulltimeStaffCount || 0
          )} • ${fmt(m.transactionRate)}%`}
          score={m.transactionScore}
          weight={criteria.transactionWeight || 40}
          active={!!m.hasMetTransactionCriterion}
          borderBottom={false}
        />
        <Flex align="center" justify="space-between" pt="14px" borderTop="1px solid" borderColor={M.border}>
          <Text fontSize="14px" fontWeight={600} color={M.baseContent}>
            Tổng điểm
          </Text>
          <Text fontSize="16px" fontWeight={700} color={M.primary}>
            {fmt(m.scorePercent ?? m.score)}/100
          </Text>
        </Flex>
      </Box>
    );
  };

  const pageBody = (
    <>
      <DetailTabBar tabs={TABS} index={tab} onChange={setTab} />
      <Box px="16px">{renderTab()}</Box>
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title="Chi tiết KPI khối" right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title="Chi tiết KPI khối" />
      {pageBody}
    </Box>
  );
};

export default BlockKpiDetail;
